import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import RenewalLayout from 'components/Renewal/RenewalLayout'
import { renewalPath } from 'components/Renewal/paths'
import { renewalRobots } from 'components/Renewal/seo'
import { consumeReloadHashSkip, hasRenewalScroll, renewalScrollKey } from 'components/Renewal/useRenewalScrollRestore'
import RenewalHomeHero from './RenewalHomeHero'
import RenewalHomeSections from './RenewalHomeSections'
import { HOME_FAQ_JSON, HOME_ORG_JSON, HOME_SEO } from './homeSeo'

export default function RenewalHomePage() {
  const location = useLocation()

  useEffect(() => {
    const root = document.querySelector('.renewal-root')
    if (!root) return

    const onClick = (event: Event) => {
      const anchor = (event.target as HTMLElement).closest('a')
      if (!anchor || !root.contains(anchor)) return
      const href = anchor.getAttribute('href') || ''
      const hashIndex = href.indexOf('#')
      if (hashIndex < 0 || hashIndex === href.length - 1) return
      const hash = href.slice(hashIndex + 1)
      const path = href.slice(0, hashIndex)
      const home = renewalPath('/')
      const here = window.location.pathname
      const onHome = here === home || here === `${home}/`
      const targetsHome = path === '' || path === home || path === `${home}/`
      if (!onHome || !targetsHome) return
      const target = document.getElementById(hash)
      if (!target) return
      event.preventDefault()
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      if (window.location.hash !== `#${hash}`) window.history.pushState(null, '', `${home}#${hash}`)
    }

    root.addEventListener('click', onClick)
    const hash = window.location.hash.slice(1)
    if (!consumeReloadHashSkip(location.pathname) && hash && !hasRenewalScroll(renewalScrollKey(location))) {
      document.getElementById(hash)?.scrollIntoView({ block: 'start' })
    }
    return () => root.removeEventListener('click', onClick)
  }, [location])

  return (
    <RenewalLayout current="home">
      <Helmet>
        <title>{HOME_SEO.title}</title>
        <meta name="description" content={HOME_SEO.description} />
        <meta name="robots" content={renewalRobots(location.pathname)} />
        <link rel="canonical" href={HOME_SEO.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={HOME_SEO.siteName} />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:title" content={HOME_SEO.title} />
        <meta property="og:description" content={HOME_SEO.description} />
        <meta property="og:url" content={HOME_SEO.canonical} />
        <meta property="og:image" content={HOME_SEO.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={HOME_SEO.imageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_SEO.title} />
        <meta name="twitter:description" content={HOME_SEO.description} />
        <meta name="twitter:image" content={HOME_SEO.image} />
        <link rel="alternate" type="application/rss+xml" title={HOME_SEO.rssTitle} href={HOME_SEO.rss} />
        <script type="application/ld+json">{JSON.stringify(HOME_ORG_JSON)}</script>
        <script type="application/ld+json">{JSON.stringify(HOME_FAQ_JSON)}</script>
      </Helmet>
      <RenewalHomeHero />
      <RenewalHomeSections />
    </RenewalLayout>
  )
}
